import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, Loader2, X, Heart, HeartHandshake, Ticket, IdCard, Bookmark } from 'lucide-react';
import { readCart, writeCart } from '../lib/shopCart';
import AccessGate from '../components/AccessGate';
import { PaymentError } from '../components/PaymentError';
import { useAuth } from '../context/AuthContext';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const CAT_ORDER = ['Membership', 'Donation', 'Events', 'Raffle', 'Merchandise'];
const CAT_ICON = { Membership: IdCard, Donation: HeartHandshake, Events: Ticket, Raffle: Ticket, Merchandise: ShoppingBag };

export default function Shop() {
  const { getShopProducts, shopCheckout, user, likeProduct, favouriteProduct, recordProductView } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => readCart().items); // id -> qty (persisted)
  const [favOnly, setFavOnly] = useState(false);
  const [fulfillment, setFulfillment] = useState('pickup');
  const [address, setAddress] = useState({ line1: '', suburb: '', state: '', postcode: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    getShopProducts().then((list) => {
      setProducts(list || []);
      (list || []).forEach((p) => recordProductView(p.id));
    }).catch(() => setProducts([])).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [customAmt, setCustomAmt] = useState(() => readCart().custom); // productId -> whole dollars (custom donations)
  useEffect(() => { writeCart({ items: cart, custom: customAmt }); }, [cart, customAmt]);
  const toggleFav = async (p) => {
    if (!user) return;
    try { const r = await favouriteProduct(p.id); setProducts((list) => list.map((x) => x.id === p.id ? { ...x, favourited: r.favourited } : x)); } catch (e) { /* ignore */ }
  };
  const unitPrice = (p) => (p.custom_amount ? (parseInt(customAmt[p.id], 10) || 0) * 100 : p.your_price);
  const add = (p) => {
    if (p.custom_amount) {
      const cents = unitPrice(p);
      if (cents < (p.min_amount || 500)) { setErr(`Please enter a whole-dollar donation of at least $${(p.min_amount || 500) / 100}.`); return; }
      setErr(''); setCart((c) => ({ ...c, [p.id]: 1 })); return;
    }
    setCart((c) => ({ ...c, [p.id]: Math.min((c[p.id] || 0) + 1, p.track_stock === false ? 99 : p.stock) }));
  };
  const inStock = (p) => p.track_stock === false || p.stock > 0;
  const CTA = { Membership: 'Renew', Donation: 'Donate', Events: 'Add', Merchandise: 'Add' };
  const visible = favOnly ? products.filter((p) => p.favourited) : products;
  const groups = CAT_ORDER.map((cat) => ({ cat, list: visible.filter((p) => (p.category || 'Merchandise') === cat) })).filter((g) => g.list.length);
  const [imgIdx, setImgIdx] = useState({}); // productId -> selected image index
  const toggleLike = async (p) => {
    if (!user) return;
    try {
      const res = await likeProduct(p.id);
      setProducts((list) => list.map((x) => x.id === p.id ? { ...x, liked: res.liked, like_count: res.like_count } : x));
    } catch (e) { /* ignore */ }
  };
  const sub = (id) => setCart((c) => { const n = (c[id] || 0) - 1; const x = { ...c }; if (n <= 0) delete x[id]; else x[id] = n; return x; });
  const items = Object.entries(cart).map(([id, qty]) => ({ p: products.find((x) => x.id === id), qty })).filter((r) => r.p);
  const subtotal = items.reduce((s, r) => s + unitPrice(r.p) * r.qty, 0);
  const postage = fulfillment === 'delivery' ? items.reduce((s, r) => s + (r.p.postage || 0) * r.qty, 0) : 0;

  const checkout = async () => {
    setErr('');
    if (!user && items.some((r) => r.p.track_stock !== false)) { setErr('Please sign in (or register) to buy merchandise or tickets. Donations can be made without signing in.'); return; }
    if (!items.length) return;
    setBusy(true);
    try {
      const res = await shopCheckout({ items: items.map((r) => ({ product_id: r.p.id, qty: r.qty, ...(r.p.custom_amount ? { amount: unitPrice(r.p) } : {}) })), fulfillment, address: fulfillment === 'delivery' ? address : null });
      window.location.href = res.checkout_url;
    } catch (e) { setErr((e.response && e.response.data && e.response.data.detail) || 'Could not start checkout.'); setBusy(false); }
  };

  return (
    <AccessGate pageKey="shop">
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="shop-page">
      <div className="flex items-center gap-2 mb-1"><ShoppingBag style={{ color: 'var(--maroon)' }} /><h1 className="font-title text-3xl" style={{ color: 'var(--maroon)' }}>Shop</h1></div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <p className="text-sm" style={{ color: 'var(--olive)' }}>{user ? 'Members get member pricing where shown. Open any item for photos and details.' : 'Prices shown are public; sign in for member pricing.'}</p>
        {user && <button onClick={() => setFavOnly(!favOnly)} data-testid="shop-fav-filter" className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border" style={{ borderColor: 'var(--navy)', color: favOnly ? 'var(--cream)' : 'var(--navy)', background: favOnly ? 'var(--navy)' : 'white' }}><Bookmark size={14} fill={favOnly ? 'currentColor' : 'none'} /> My favourites ({products.filter((p) => p.favourited).length})</button>}
      </div>
      {loading ? <div className="flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={16} /> Loading…</div> : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {products.length === 0 && <p className="text-sm" style={{ color: 'var(--olive)' }}>No products yet.</p>}
            {groups.map(({ cat, list }) => { const Icon = CAT_ICON[cat] || ShoppingBag; return (
            <div key={cat} data-testid={`shop-group-${cat}`}>
              <h2 className="flex items-center gap-2 font-title text-lg mb-2" style={{ color: 'var(--navy)' }}><Icon size={18} /> {cat}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
            {list.map((p) => (
              <div key={p.id} className="bg-white rounded-lg border shadow-sm overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`product-${p.id}`}>
                {(() => {
                  const imgs = (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []));
                  const idx = Math.min(imgIdx[p.id] || 0, Math.max(0, imgs.length - 1));
                  return imgs.length ? (
                    <div>
                      <Link to={p.link || `/shop/${p.id}`} data-testid={`product-open-${p.id}`}><img src={imgs[idx]} alt={p.name} className="w-full h-44 object-cover hover:opacity-90" data-testid={`product-main-image-${p.id}`} /></Link>
                      {imgs.length > 1 && (
                        <div className="flex gap-1 p-1.5 flex-wrap" data-testid={`product-thumbs-${p.id}`}>
                          {imgs.map((im, i) => (
                            <button key={i} onClick={() => setImgIdx((s) => ({ ...s, [p.id]: i }))} data-testid={`product-thumb-${p.id}-${i}`}
                              className="w-9 h-9 rounded overflow-hidden border" style={{ borderColor: i === idx ? 'var(--maroon)' : 'rgba(0,0,0,0.15)' }}>
                              <img src={im} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : <div className="w-full h-2" style={{ background: 'var(--sage)' }} />;
                })()}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-title text-lg"><Link to={p.raffle_id ? `/raffle/${p.raffle_id}` : (p.link || `/shop/${p.id}`)} className="hover:underline" style={{ color: 'var(--navy)' }} data-testid={`product-title-${p.id}`}>{p.name}</Link></h3>
                    <div className="flex items-center gap-2 shrink-0">
                      <label className="flex items-center gap-1 text-sm cursor-pointer select-none" title={user ? 'Like this item' : 'Sign in to like'} data-testid={`like-${p.id}`}>
                        <input type="checkbox" disabled={!user} checked={!!p.liked} onChange={() => toggleLike(p)} data-testid={`like-checkbox-${p.id}`} className="accent-[var(--maroon)]" />
                        <Heart size={15} fill={p.liked ? 'var(--maroon)' : 'none'} style={{ color: 'var(--maroon)' }} />
                        <span data-testid={`like-count-${p.id}`} style={{ color: 'var(--olive)' }}>{p.like_count || 0}</span>
                      </label>
                      <button onClick={() => toggleFav(p)} disabled={!user} title={user ? (p.favourited ? 'Remove from favourites' : 'Save to favourites') : 'Sign in to save favourites'} data-testid={`fav-${p.id}`} className="disabled:opacity-40">
                        <Bookmark size={15} fill={p.favourited ? 'var(--navy)' : 'none'} style={{ color: 'var(--navy)' }} />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs mb-2 line-clamp-2" style={{ color: 'var(--olive)' }}>{p.description}</p>
                  <div className="flex items-center justify-between gap-2">
                    {p.custom_amount ? (
                      <label className="flex items-center gap-1 font-title text-lg" style={{ color: 'var(--maroon)' }}>$
                        <input type="number" min={(p.min_amount || 500) / 100} step="1" inputMode="numeric" placeholder="25" value={customAmt[p.id] || ''}
                          onChange={(e) => setCustomAmt({ ...customAmt, [p.id]: e.target.value.replace(/\D/g, '') })}
                          data-testid={`custom-amount-${p.id}`} className="w-20 px-2 py-1 rounded border text-base" style={{ borderColor: 'rgba(0,0,0,0.2)', color: 'var(--ink)' }} />
                      </label>
                    ) : (
                    <div>
                      <span className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{money(p.your_price)}</span>
                      {p.member_eligible && p.price_member < p.price_public && <span className="text-xs line-through ml-1" style={{ color: 'var(--olive)' }}>{money(p.price_public)}</span>}
                    </div>
                    )}
                    {p.raffle_id ? (
                      <Link to={`/raffle/${p.raffle_id}`} data-testid={`raffle-open-${p.id}`} className="px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>Buy tickets</Link>
                    ) : inStock(p) ? (
                      <button onClick={() => add(p)} data-testid={`add-${p.id}`} className="px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{CTA[p.category] || 'Add'}</button>
                    ) : <span className="text-xs font-title" style={{ color: '#8a1f11' }}>Sold out</span>}
                  </div>
                  {p.track_stock !== false && <p className="text-[11px] mt-1" style={{ color: 'var(--olive)' }}>{p.stock} in stock{p.postage ? ` · +${money(p.postage)} postage` : ''}</p>}
                </div>
              </div>
            ))}
              </div>
            </div>
            ); })}
          </div>
          <div className="bg-white rounded-lg border shadow-sm p-4 h-fit" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="cart">
            <h3 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>Your cart</h3>
            <PaymentError err={err} testid="shop-error" className="text-xs mb-2 px-2 py-1.5 rounded" />
            {items.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }}>Cart is empty.</p> : (
              <>
                {items.map((r) => (
                  <div key={r.p.id} className="flex items-center justify-between text-sm mb-2">
                    <span className="flex-1">{r.p.name}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => sub(r.p.id)} data-testid={`minus-${r.p.id}`}><Minus size={14} /></button>
                      <span data-testid={`qty-${r.p.id}`}>{r.qty}</span>
                      <button onClick={() => add(r.p)} data-testid={`plus-${r.p.id}`}><Plus size={14} /></button>
                    </div>
                    <span className="w-16 text-right">{money(unitPrice(r.p) * r.qty)}</span>
                  </div>
                ))}
                <div className="mt-3 mb-2">
                  <label className="flex items-center gap-2 text-sm"><input type="radio" checked={fulfillment === 'pickup'} onChange={() => setFulfillment('pickup')} data-testid="fulfil-pickup" /> Pickup (free)</label>
                  <label className="flex items-center gap-2 text-sm"><input type="radio" checked={fulfillment === 'delivery'} onChange={() => setFulfillment('delivery')} data-testid="fulfil-delivery" /> Delivery (+{money(postage)})</label>
                </div>
                {fulfillment === 'delivery' && (
                  <div className="space-y-1 mb-2">
                    <input placeholder="Address" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} data-testid="addr-line1" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                    <div className="flex gap-1">
                      <input placeholder="Suburb" value={address.suburb} onChange={(e) => setAddress({ ...address, suburb: e.target.value })} className="flex-1 px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                      <input placeholder="Postcode" value={address.postcode} onChange={(e) => setAddress({ ...address, postcode: e.target.value })} className="w-24 px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                )}
                <div className="flex justify-between font-title text-sm mt-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                  <span>Total</span><span data-testid="cart-total">{money(subtotal + postage)}</span>
                </div>
                <button onClick={checkout} disabled={busy} data-testid="checkout-btn" className="w-full mt-3 py-2 rounded font-title inline-flex items-center justify-center gap-2" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
                  {busy && <Loader2 size={14} className="animate-spin" />} Checkout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
    </AccessGate>
  );
}
